import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';

import {
  FileConfig,
  FileTypes,
  FileUpload,
  IFileConfig,
  IValidationMessage,
} from '@models/file';
import { FormConfig, IFormConfig } from '@models/form/form';
import { NotificationService } from '@core/services/notification.service';
import { SaveCancelButtonConfig } from '@models/form/button';
import { FileUploadService } from '@app/infrastructure/core/services/api/file-upload.service';
import { BehaviorSubject } from 'rxjs';
import { flatMap } from 'rxjs/operators';

@Component({
  selector: 'app-base-uploader',
  templateUrl: './base-uploader.component.html',
  styleUrls: ['./base-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseUploaderComponent implements OnInit {
  @Input() public currentImageFile: string = '';
  @Input() public fileConfig: IFileConfig = new FileConfig();
  @Input() public fileListFormControl: AbstractControl;
  @Input() public form: UntypedFormGroup = new UntypedFormGroup({});
  @Input() public formConfig: IFormConfig = new FormConfig();
  @Input() public validationMessageList: IValidationMessage[] = [];
  @Input() public disabled: boolean = false;
  @Input() public required: boolean = false;

  @Output() public emitFileSelect = new EventEmitter<FileList>();
  @Output() public fileUploadsEmitter = new EventEmitter<FileUpload[]>();
  @ViewChild('input') fileInputRef: ElementRef;

  /**
   * Set a unique value as the file input id to prevent collisions
   */
  public fileId: string;
  public imageUrl: string | ArrayBuffer = '';
  public fileProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
  public isUploading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  constructor(
    private cd: ChangeDetectorRef,
    private notificationService: NotificationService,
    private fileUploadService: FileUploadService,
  ) {}

  ngOnInit(): void {
    this.fileId = uuidv4();
  }

  public onFileSelect(fileList: FileList): void {
    const file = fileList[0];
    if (!file) {
      this.notificationService.showError(['Please select a file.']);
    } else if (!this.fileConfig.canUploadMultiple && fileList?.length > 1) {
      this.notificationService.showError(['Please select only one file.']);
    } else {
      this.emitFileSelect.emit(fileList);
      this.formConfig.submit = new SaveCancelButtonConfig({
        save: this.updateSubmitButtonText(),
      });
      this.fileUploadService
        .createFileUpload(fileList)
        .pipe(
          flatMap((upload: FileUpload) => {
            this.isUploading$.next(true);
            return this.uploadToS3(upload, file, (progress: number) => {
              this.fileProgress$.next(progress);
              if (progress >= 100) {
                this.isUploading$.next(false);
                this.imageUrl = upload.signedUrl;
                this.cd.markForCheck();
              }
              return upload;
            });
          }),
          flatMap((upload: FileUpload) => {
            return this.fileUploadService.getFileUpload(upload.id);
          }),
        )
        .subscribe(
          (upload: FileUpload) => {
            this.fileUploadsEmitter.emit([upload]);
            if (upload.fileType === FileTypes.IMAGE)
              this.currentImageFile = upload.signedUrl;
            this.cd.markForCheck();
          },
          (error) => {
            this.notificationService.showError([
              'Could not upload file try again.',
            ]);
            this.isUploading$.next(false);
            this.fileProgress$.next(0);
          },
        );

      this.previewImageUpload(fileList);
      this.fileListFormControl.markAsTouched();
    }
  }

  public onFileClear(): void {
    this.fileListFormControl.reset();
    this.fileInputRef.nativeElement.value = null;
    this.imageUrl = '';
    this.emitFileSelect.emit(null);
  }

  public updateSubmitButtonText(): string {
    const fileOrFiles =
      this.fileListFormControl.value?.length > 1 ? 'files' : 'file';
    return this.fileConfig.canUploadMultiple &&
      this.form.valid &&
      this.fileListFormControl.value?.length
      ? `Upload ${this.fileListFormControl.value?.length} ${fileOrFiles}`
      : 'Upload';
  }

  private uploadFileAndShowProgress(): void {
    const formData = new FormData();
    const files: FileList = this.fileListFormControl.value;
    if (files.length <= 0) return;
    for (let index = 0; index < files.length; index++) {
      formData.append('files', files[index]);
    }
    this.fileUploadService
      .createFileUpload(files)
      .subscribe((upload: FileUpload) => {
        console.log(upload);
      });
  }

  private previewImageUpload(fileList: FileList): void {
    this.imageUrl = '';
    if (fileList && fileList[0]) {
      const file = fileList[0];
      const mimeType = file.type;
      if (mimeType.match(/image\/*/) !== null) {
        if (fileList.length === 1) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = (event) => {
            this.imageUrl = event.target.result;
            this.cd.markForCheck();
          };
        }
      }
    }
  }
  private uploadToS3(
    upload: FileUpload,
    file: File,
    progressCallback: Function,
  ): Promise<FileUpload> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve(upload);
          } else {
            reject(xhr.response);
          }
        }
      };
      if (progressCallback) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            var percentComplete = Math.round((e.loaded / file.size) * 100);
            progressCallback(percentComplete);
          }
        };
      }

      xhr.open('PUT', upload.signedUrl);
      xhr.send(file);
    });
  }
} //
